import { BudgetLine, BudgetOverrides, ManualMatchMap } from '../data/budgetData';

const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';
const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';

const headers = (): HeadersInit => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${API_KEY}`,
});

const url = (path: string) => `${API_BASE}${path}`;

export interface BudgetRow {
  id: number;
  entity: string;
  fiscal_year: number;
  no_acc: string;
  keterangan: string;
  cabang: string;
  month: number;
  amount: number;
  is_locked: number;
  is_custom: number;
  updated_by: string | null;
  updated_at: string;
}

export interface AuditRow {
  id: number;
  action: string;
  entity: string | null;
  no_acc: string | null;
  keterangan: string | null;
  old_amount: number | null;
  new_amount: number | null;
  changed_by: string | null;
  changed_at: string;
  source: string | null;
  details: string | null;
}

// Convert API rows -> BudgetOverrides map (matches local shape used by useBudget)
export function rowsToOverrides(rows: BudgetRow[]): Record<string, any> {
  const map: Record<string, any> = {};
  rows.forEach(r => {
    const key = `${r.no_acc}__${r.keterangan}`;
    if (r.is_custom) {
      map[key] = {
        noAcc: r.no_acc,
        nameAccount: '',
        keterangan: r.keterangan,
        keteranganDetail: '',
        defaultAmount: 0,
        isKondisional: false,
        amount: r.amount,
        isLocked: !!r.is_locked,
        user: r.updated_by ?? '',
      };
    } else {
      map[key] = { amount: r.amount, isLocked: !!r.is_locked };
    }
  });
  return map;
}

export async function fetchBudget(entity: string, year: number): Promise<BudgetRow[]> {
  const res = await fetch(url(`/api/budget?entity=${entity}&year=${year}`), { headers: headers() });
  if (!res.ok) throw new Error(`fetchBudget ${res.status}`);
  return res.json();
}

export async function putBudgetLine(payload: {
  entity: string;
  fiscal_year: number;
  no_acc: string;
  keterangan: string;
  cabang?: string;
  month?: number;
  amount: number;
  is_locked?: boolean;
  is_custom?: boolean;
  updated_by?: string;
}): Promise<void> {
  const res = await fetch(url('/api/budget'), {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`putBudgetLine ${res.status}`);
}

export async function deleteBudgetLine(payload: {
  entity: string;
  fiscal_year: number;
  no_acc: string;
  keterangan: string;
  cabang?: string;
  month?: number;
  updated_by?: string;
}): Promise<void> {
  const res = await fetch(url('/api/budget'), {
    method: 'DELETE',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`deleteBudgetLine ${res.status}`);
}

export async function importBudgetLines(payload: {
  entity: string;
  fiscal_year: number;
  lines: BudgetLine[];
  strategy: 'replace' | 'skip';
  updated_by?: string;
}): Promise<void> {
  const apiLines = payload.lines.map(l => ({
    noAcc: l.noAcc,
    keterangan: l.keterangan,
    cabang: 'ALL',
    month: 0,
    amount: l.amount,
    isLocked: l.isLocked,
    isCustom: true,
  }));
  const res = await fetch(url('/api/budget/import'), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({
      entity: payload.entity,
      fiscal_year: payload.fiscal_year,
      lines: apiLines,
      strategy: payload.strategy,
      updated_by: payload.updated_by,
    }),
  });
  if (!res.ok) throw new Error(`importBudgetLines ${res.status}`);
}

export async function fetchManualMatch(entity: string): Promise<ManualMatchMap> {
  const res = await fetch(url(`/api/manual-match?entity=${entity}`), { headers: headers() });
  if (!res.ok) throw new Error(`fetchManualMatch ${res.status}`);
  return res.json();
}

export async function putManualMatch(payload: {
  entity: string;
  gl_row_index: number;
  budget_key: string;
  updated_by?: string;
}): Promise<void> {
  const res = await fetch(url('/api/manual-match'), {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`putManualMatch ${res.status}`);
}

// ---- Budget Summary (BudgetVariancePage 6 categories) ----

export async function fetchBudgetSummary(scope = '__global__'): Promise<Record<string, number>> {
  const res = await fetch(url(`/api/budget-summary?scope=${encodeURIComponent(scope)}`), { headers: headers() });
  if (!res.ok) throw new Error(`fetchBudgetSummary ${res.status}`);
  return res.json();
}

export async function putBudgetSummary(payload: {
  scope?: string;
  data: Record<string, number>;
  updated_by?: string;
}): Promise<void> {
  const res = await fetch(url('/api/budget-summary'), {
    method: 'PUT',
    headers: headers(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`putBudgetSummary ${res.status}`);
}

export async function migrateLocalStorageSummaryToCloud(scope: string, currentUser: string): Promise<{ migrated: number } | null> {
  const raw = localStorage.getItem('erp_budget_data');
  if (!raw) return null;
  let parsed: Record<string, number>;
  try { parsed = JSON.parse(raw); } catch { return null; }
  const keys = Object.keys(parsed);
  if (keys.length === 0) return null;

  await putBudgetSummary({ scope, data: parsed, updated_by: currentUser });
  localStorage.setItem(`erp_budget_data__backup_${Date.now()}`, raw);
  localStorage.removeItem('erp_budget_data');
  return { migrated: keys.length };
}

export async function fetchAudit(entity?: string, limit = 100): Promise<AuditRow[]> {
  const qs = new URLSearchParams();
  if (entity) qs.set('entity', entity);
  qs.set('limit', String(limit));
  const res = await fetch(url(`/api/audit?${qs.toString()}`), { headers: headers() });
  if (!res.ok) throw new Error(`fetchAudit ${res.status}`);
  return res.json();
}

// One-time migration helper — push existing localStorage data to cloud
export async function migrateLocalStorageToCloud(entity: string, year: number, currentUser: string): Promise<{ migrated: number } | null> {
  const overridesRaw = localStorage.getItem('erp_budget_overrides_v1');
  if (!overridesRaw) return null;
  let parsed: BudgetOverrides | Record<string, any>;
  try { parsed = JSON.parse(overridesRaw); } catch { return null; }
  const entries = Object.entries(parsed);
  if (entries.length === 0) return null;

  const lines: BudgetLine[] = entries.map(([key, v]: [string, any]) => {
    const [noAcc, keterangan] = key.split('__');
    if (v.noAcc && v.keterangan) {
      return { ...v, isLocked: !!v.isLocked };
    }
    return {
      noAcc, nameAccount: '', keterangan, keteranganDetail: '',
      defaultAmount: 0, isKondisional: false,
      amount: v.amount ?? 0, isLocked: !!v.isLocked, user: currentUser,
    };
  });

  await importBudgetLines({ entity, fiscal_year: year, lines, strategy: 'replace', updated_by: currentUser });
  // Backup before clearing
  localStorage.setItem(`erp_budget_overrides_v1__backup_${Date.now()}`, overridesRaw);
  localStorage.removeItem('erp_budget_overrides_v1');
  return { migrated: lines.length };
}
